// LanguageButton.jsx
import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';

import getCurrentLanguage, {
  getLanguageTranslationKey,
} from '../resource/getCurrentLanguage.js';
import { ButtonId } from '../resource/mixpanel.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

import ResetButtonStyle from '../style/ResetButtonStyle.js';
import { breakpoint, color } from '../style/variables.js';

import LanguageTealblueIconSource from '../../img/ic_language_tealblue.svg';
import LanguageWhiteIconSource from '../../img/ic_language_white.svg';
import NextTealblueIconSource from '../../img/ic_next_tealblue.svg';
import NextWhiteIconSource from '../../img/ic_next_white.svg';

import { NULL_FUNCTION } from '../resource/defaults.js';

export const LayoutType = {
  HAMBURGER: 'hamburger',
};

export const LanguageButton = ({
  modalViewRootId = null,
  layoutType = null,
  isLanguageMenuOpened = false,
  deviceWidth = 0,
  t,
  i18n,
  toggleModal = NULL_FUNCTION,
}) => {
  const isHamburger = LayoutType.HAMBURGER === layoutType;
  const isMobile = deviceWidth <= breakpoint.mobile;
  const closeModalOnItemClick = isMobile
    ? NULL_FUNCTION
    : () =>
        toggleModal({
          modalId: 'HamburgerMenuModal',
          isOpen: false,
        });

  // eslint-disable-next-line no-console
  console.log('LanguageButton', { deviceWidth, isMobile });
  return (
    <StyledLanguageButton
      isHamburger={isHamburger}
      id={modalViewRootId}
      data-element_id={ButtonId.Setting.ButtonLanguage}
      onClick={event => {
        event.stopPropagation();
        closeModalOnItemClick();
        return toggleModal({
          modalId: 'LanguageMenu',
          isOpen: !isLanguageMenuOpened,
          payload: {
            modalViewRootId,
          },
        });
      }}
      isMenuOpened={isLanguageMenuOpened}
    >
      <Title isHamburger={isHamburger} isMenuOpened={isLanguageMenuOpened}>
        <LanguageIcon
          isHamburger={isHamburger}
          alt="language icon"
          src={
            isHamburger
              ? LanguageWhiteIconSource.src || LanguageWhiteIconSource
              : isLanguageMenuOpened
                ? LanguageTealblueIconSource.src || LanguageTealblueIconSource
                : LanguageWhiteIconSource.src || LanguageWhiteIconSource
          }
        />
        {isHamburger ? (
          <span>
            {t('language', {
              ns: TranslationNamespace.SETTINGS,
            })}
          </span>
        ) : null}
      </Title>
      <SelectedLanguage
        isHamburger={isHamburger}
        isMenuOpened={isLanguageMenuOpened}
      >
        <span>
          {t(
            getLanguageTranslationKey({
              language: i18n.language || getCurrentLanguage(),
            }),
            {
              ns: TranslationNamespace.SETTINGS,
            }
          )}
        </span>
        {!isHamburger && (
          <NextIcon
            width={24}
            height={24}
            alt="switch language"
            src={
              isLanguageMenuOpened
                ? NextTealblueIconSource.src || NextTealblueIconSource
                : NextWhiteIconSource.src || NextWhiteIconSource
            }
          />
        )}
      </SelectedLanguage>
    </StyledLanguageButton>
  );
};

LanguageButton.propTypes = {
  modalViewRootId: PropTypes.string,
  layoutType: PropTypes.string,
  isLanguageMenuOpened: PropTypes.bool,
  deviceWidth: PropTypes.number,
  i18n: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  toggleModal: PropTypes.func,
};

const StyledLanguageButton = styled.button.attrs(
  ({ isMenuOpened, isHamburger }) => ({
    style: {
      borderColor: isMenuOpened ? color.tealBlue : color.neutral[100],
      borderWidth: isHamburger ? 'medium' : '1px',
      borderStyle: isHamburger ? 'none' : 'solid',
      color: isMenuOpened ? color.tealBlue : color.neutral[100],
      width: isHamburger ? '100%' : 'width',
      height: isHamburger ? 'unset' : '36px',
      padding: isHamburger ? '0' : '0px 8px',
      lineHeight: 1.5,
    },
  })
)`
  ${ResetButtonStyle};
  position: relative;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Title = styled.div.attrs(({ isHamburger, isMenuOpened }) => ({
  style: {
    color: isHamburger
      ? color.neutral[100]
      : isMenuOpened
        ? color.tealBlue
        : color.neutral[100],
  },
}))`
  display: flex;
  align-items: center;
  font-size: 14px;
`;

const LanguageIcon = styled.img.attrs(({ isHamburger }) => ({
  style: {
    marginRight: `${isHamburger ? 8 : 4}px`,
    width: isHamburger ? 'unset' : '16px',
    height: isHamburger ? 'unset' : '16px',
  },
}))`
  object-fit: contain;
`;

const SelectedLanguage = styled.div.attrs(({ isHamburger, isMenuOpened }) => ({
  style: {
    color: isHamburger
      ? color.neutral[30]
      : isMenuOpened
        ? color.tealBlue
        : color.neutral[100],
  },
}))`
  display: flex;
  align-items: center;
  font-size: 14px;
`;

const NextIcon = styled.img.attrs(() => ({
  style: {
    width: '24px',
    height: '24px',
  },
}))`
  margin-left: 4px;
  object-fit: contain;
`;

export default withTranslation()(LanguageButton);
